<script setup>
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue';
import Select from '@/Components/ShopManagement/Select.vue';
import Message from 'primevue/message';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import { ADMIN_WORKSPACE } from '@/BayHiveAdmin/workspace.js';
import { selectionKey } from '@/BayHiveAdmin/selection.js';
const workspace = inject(ADMIN_WORKSPACE);
const allAccounts = { type: 'all', key: 'all:', name: 'All accounts' };
const currentChoice = computed(() => {
  const current = workspace.selection.value;
  if (current.type === 'all') return allAccounts;
  return {
    ...current,
    key: selectionKey(current),
    name:
      current.name ||
      (current.id
        ? `${current.type === 'mso' ? 'MSO' : 'Shop'} #${current.id}`
        : 'Select accounts'),
  };
});
const selection = ref(currentChoice.value);
const suggestions = ref([]);
const error = ref('');
const selectionError = ref('');
const loading = ref(false);
const switching = ref(false);
const searched = ref(false);
const lastQuery = ref('');
const options = computed(() => {
  const records = [allAccounts, ...suggestions.value];
  if (!lastQuery.value && ['shop', 'mso'].includes(currentChoice.value.type))
    records.splice(1, 0, currentChoice.value);
  return [...new Map(records.map((record) => [selectionKey(record), record])).values()].map(
    (record) => ({
      ...record,
      key: selectionKey(record),
      // The search service already matches names and IDs.
      searchQuery: lastQuery.value,
    }),
  );
});
const choiceIcon = (choice) =>
  choice.type === 'mso'
    ? 'fa-solid fa-building'
    : choice.type === 'shop'
      ? 'fa-solid fa-store'
      : 'fa-solid fa-globe';
const choiceDetails = (choice) => {
  if (choice.type === 'all') return 'Global';
  if (choice.type === 'shop') return `Shop · #${choice.id}`;
  if (choice.type === 'mso') {
    const count = choice.locationCount;
    return `MSO · #${choice.id}${Number.isInteger(count) ? ` · ${count} location${count === 1 ? '' : 's'}` : ''}`;
  }
  return 'Choose a shop or MSO';
};
let sequence = 0;
async function search(query = '') {
  const current = ++sequence;
  lastQuery.value = query || '';
  error.value = '';
  searched.value = false;
  suggestions.value = [];
  if (!workspace.state.canSearch) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const results = await workspace.search(query?.trim() || '');
    if (current === sequence) {
      suggestions.value = results
        .map((result) => ({ ...result, type: result.type || 'shop' }))
        .sort(
          (a, b) =>
            (a.type === 'mso' ? 0 : 1) - (b.type === 'mso' ? 0 : 1) || a.name.localeCompare(b.name),
        );
      searched.value = true;
    }
  } catch {
    if (current === sequence) {
      error.value = 'Shops and MSOs could not be loaded.';
    }
  } finally {
    if (current === sequence) loading.value = false;
  }
}
async function select(value) {
  // Select updates its internal value before emitting. Reassert the actual
  // scope while the pending-edit guard and navigation are still in progress.
  selection.value = { ...currentChoice.value };
  if (switching.value || selectionKey(value) === selectionKey(currentChoice.value)) return;
  selectionError.value = '';
  switching.value = true;
  try {
    const next = { ...value };
    delete next.key;
    delete next.label;
    delete next.searchQuery;
    await workspace.switchSelection(next);
  } catch {
    selectionError.value = 'The account selection could not be changed. Try again.';
  } finally {
    selection.value = { ...currentChoice.value };
    switching.value = false;
  }
}
function clearSearch() {
  sequence++;
  lastQuery.value = '';
  suggestions.value = [];
  error.value = '';
  loading.value = false;
  searched.value = false;
}
onBeforeUnmount(() => {
  sequence++;
});
watch(currentChoice, (value) => {
  selection.value = value;
  selectionError.value = '';
  clearSearch();
});
</script>

<template>
  <div class="admin-shop-search">
    <Select
      :model-value="selection"
      :options="options"
      option-label="name"
      data-key="key"
      remote
      :filter="workspace.state.canSearch"
      :filter-fields="['searchQuery']"
      reset-filter-on-hide
      filter-placeholder="Search shops or MSOs"
      filter-icon="fa-solid fa-magnifying-glass"
      dropdown-icon="fa-solid fa-chevron-down"
      aria-label="Choose all accounts, an MSO, or a shop"
      size="small"
      fluid
      scroll-height="var(--p-bayhive-layout-topbar-scope-options-height)"
      :disabled="workspace.state.loading || workspace.state.navigationBusy || switching"
      @show="!loading && !searched && search(lastQuery)"
      @hide="clearSearch"
      @filter="search($event.value)"
      @update:model-value="select"
    >
      <template #value>
        <span
          class="admin-shop-search-option"
          :title="`${currentChoice.name} · ${choiceDetails(currentChoice)}`"
        >
          <i :class="choiceIcon(currentChoice)" aria-hidden="true" />
          <span class="admin-shop-search-copy">
            <span class="admin-shop-search-name">{{ currentChoice.name }}</span>
            <small>{{ choiceDetails(currentChoice) }}</small>
          </span>
        </span>
      </template>
      <template #option="{ option }">
        <span class="admin-shop-search-option">
          <i :class="choiceIcon(option)" aria-hidden="true" />
          <span class="admin-shop-search-copy">
            <span class="admin-shop-search-name">{{ option.name }}</span>
            <small>{{ choiceDetails(option) }}</small>
          </span>
        </span>
      </template>
      <template #footer>
        <div
          v-if="loading || error || !workspace.state.canSearch || (searched && !suggestions.length)"
          class="admin-shop-search-status"
        >
          <div
            v-if="loading"
            class="admin-shop-search-loading"
            role="status"
            aria-busy="true"
            aria-label="Loading shops and MSOs"
          >
            <span class="admin-shop-search-option"><Skeleton width="1em" height="1em" /><span class="admin-shop-search-copy"><span class="admin-shop-search-name"><Skeleton width="12rem" height="1em" /></span><small><Skeleton width="8rem" height="1em" /></small></span></span>
          </div>
          <Message v-else-if="error" severity="error" size="small" variant="simple">
            {{ error }}
            <Button label="Retry search" size="small" text @click="search(lastQuery)" />
          </Message>
          <Message
            v-else-if="!workspace.state.canSearch"
            severity="info"
            size="small"
            variant="simple"
          >
            Shop and MSO search is currently unavailable.
          </Message>
          <Message v-else severity="info" size="small" variant="simple">
            No shops or MSOs match your search.
          </Message>
        </div>
      </template>
    </Select>
    <Message v-if="selectionError" severity="error" size="small" variant="simple">
      {{ selectionError }}
    </Message>
  </div>
</template>

<style scoped>
.admin-shop-search-option {
  display: flex;
  align-items: center;
  gap: var(--p-bayhive-spacing-sm);
  min-width: 0;
}
.admin-shop-search-option > i {
  flex: 0 0 auto;
}
.admin-shop-search-copy {
  display: grid;
  min-width: 0;
}
.admin-shop-search-name,
.admin-shop-search-option small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.admin-shop-search-option small {
  display: block;
  color: var(--p-text-muted-color);
}
.admin-shop-search-status {
  padding: var(--p-bayhive-spacing-sm);
}
.admin-shop-search-loading {
  display: grid;
  gap: var(--p-bayhive-spacing-xs);
}
.admin-shop-search {
  display: grid;
  gap: var(--p-bayhive-spacing-xs);
  width: 100%;
  min-width: 0;
  max-width: 100%;
}
</style>
